import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useFetchJobsFromFeed,
  useFetchJobsFromCache,
  useClearJobFeedCache,
} from 'src/api/hooks/useJob';
import { ParsedJobItem } from 'src/api/hooks/useJob.types';
import { useStoreActions } from 'src/stores';
import { PopUpEnum } from 'src/stores/NotificationStore';
import { validateXMLurl } from 'src/utils/validateXML';

export const useXmlFeedImport = () => {
  const { t } = useTranslation();

  const setPopUpData = useStoreActions(action => action.popUp.setPopUp);
  const [parsedJobs, setParsedJobs] = useState<ParsedJobItem[]>([]);
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set());
  const [currentImportIndex, setCurrentImportIndex] = useState<number>(0);
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [isFeedDialogOpen, setIsFeedDialogOpen] = useState<boolean>(false);
  const [isFetchingUrl, setIsFetchingUrl] = useState<boolean>(false);

  const [url, setUrl] = useState<string>('');
  const [error, setError] = useState<string>('');

  const onComplete = (res: { jobs: ParsedJobItem[] } | undefined) => {
    if (!res) return;

    const jobs = res.jobs;

    if (jobs.length === 0) {
      setParsedJobs([]);
      setSelectedIndexes(new Set());
      setIsFetchingUrl(false);
      return;
    }

    setParsedJobs(jobs);
    setSelectedIndexes(new Set(jobs.map((_, i) => i)));
    setIsFetchingUrl(false);
  };

  const onError = () => {
    if (url) {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('jobPage.feedDialog.fetchError'),
      });
    }
    setIsFetchingUrl(false);
  };

  const [fetchJobsFromFeed, { loading: fetchingFromFeedLoading }] = useFetchJobsFromFeed({
    onComplete,
    onError,
  });

  const [fetchJobsFromCache, { loading: fetchingFromCacheLoading }] = useFetchJobsFromCache({
    onComplete,
    onError,
  });

  const resetState = () => {
    setParsedJobs([]);
    setSelectedIndexes(new Set());
    setCurrentImportIndex(0);
    setIsImporting(false);
    setUrl('');
    setError('');
  };

  const [clearJobFeedCache, { loading: clearingCacheLoading }] = useClearJobFeedCache({
    onComplete: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.success,
        message: t('jobPage.feedDialog.clearCacheSuccess'),
      });
    },
    onError: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('jobPage.feedDialog.clearCacheError'),
      });
    },
  });

  const selectedJobs = useMemo(
    () => parsedJobs.filter((_, i) => selectedIndexes.has(i)),
    [parsedJobs, selectedIndexes],
  );
  const totalSelected = selectedJobs.length;
  const hasJobs = parsedJobs.length > 0;
  const isFetchingFeed =
    fetchingFromFeedLoading || fetchingFromCacheLoading || isFetchingUrl || clearingCacheLoading;

  const openFeedDialog = () => {
    setIsFeedDialogOpen(true);
  };

  const closeFeedDialog = () => {
    setIsFeedDialogOpen(false);
  };

  const resetFeed = useCallback(() => {
    resetState();
    clearJobFeedCache();
  }, [clearJobFeedCache]);

  const handleFeedUrlSubmit = (url: string) => {
    setIsFetchingUrl(true);
    try {
      fetchJobsFromFeed({ variables: { url } });
    } catch (error) {
      console.error('Error fetching XML:', (error as Error).message);
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('jobPage.feedDialog.fetchError'),
      });
      setIsFetchingUrl(false);
    }
  };

  const toggleJobSelection = useCallback((index: number) => {
    setSelectedIndexes(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIndexes(new Set(parsedJobs.map((_, i) => i)));
  }, [parsedJobs]);

  const deselectAll = useCallback(() => {
    setSelectedIndexes(new Set());
  }, []);

  const startImporting = () => {
    if (totalSelected === 0) return;
    setCurrentImportIndex(0);
    setIsImporting(true);
    setIsFeedDialogOpen(false);
  };

  const advanceImportIndex = useCallback(() => {
    setCurrentImportIndex(prev => prev + 1);
  }, []);

  const stopImporting = () => {
    setIsImporting(false);
    setCurrentImportIndex(0);
  };

  const handleUrlChange = (e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setUrl(value);
    setError(!value || validateXMLurl(value) ? '' : t('jobPage.feedDialog.errorInvalid'));
  };

  const handleClose = () => {
    if (isFetchingFeed) return;
    stopImporting();
    setUrl('');
    setError('');
    closeFeedDialog();
  };

  const handleSubmit = () => {
    const trimmedUrl = url.trim();

    if (!trimmedUrl) {
      setError(t('jobPage.feedDialog.errorEmpty'));
      return;
    }

    if (!validateXMLurl(trimmedUrl)) {
      setError(t('jobPage.feedDialog.errorInvalid'));
      return;
    }

    handleFeedUrlSubmit(trimmedUrl);
  };

  return {
    parsedJobs,
    selectedIndexes,
    currentImportIndex,
    totalSelected,
    hasJobs,
    isImporting,
    isFetchingFeed,
    isFeedDialogOpen,
    openFeedDialog,
    closeFeedDialog,
    handleFeedUrlSubmit,
    toggleJobSelection,
    selectAll,
    deselectAll,
    startImporting,
    stopImporting,
    advanceImportIndex,
    fetchJobsFromFeed,
    fetchJobsFromCache,
    resetFeed,
    handleUrlChange,
    handleClose,
    handleSubmit,
    url,
    error,
  };
};
